import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import confetti from "canvas-confetti";
import { Body, Eyebrow, Heading } from "components/ui/Text";
import UILabel from "components/ui/UILabel";
import { ButtonLevel, ButtonRouterLink } from "components/ui/Button";
import { colors, zIndex } from "utils/theme";
import {
  getIncomeTaxReturns,
  usePostNotificationMarkAsReadMutation,
} from "store/api/apiSlice";
import AnimatedCounter from "components/ui/AnimatedCounter";
import { ApiNotification } from "utils/api/notifications";
import Modal, { ModalApi } from "../Modal";
import * as Styled from "./styled";

const throwConfetti = () => {
  confetti({
    particleCount: 120,
    shapes: ["circle"],
    colors: [colors.azure, colors.midnight, colors.heliotrope, colors.violet],
    spread: 500,
    origin: { y: 0.4 },
    ticks: 400,
    zIndex: zIndex.modal,
  });
};

const getReturnTitle = (jurisdiction: string) => {
  if (jurisdiction === "Federal") return "federal";

  return `${jurisdiction} state`;
};

export interface IncomeTaxReturnAcceptedNotificationModalApi {
  show: (notification: ApiNotification) => void;
  dismiss: () => void;
}

const IncomeTaxReturnAcceptedNotificationModal = (
  _: {},
  forwardedRef: React.ForwardedRef<unknown>
) => {
  const ref = useRef<ModalApi>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [notification, setNotification] = useState<ApiNotification>(null);
  const [markAsRead] = usePostNotificationMarkAsReadMutation();
  const { data: incomeTaxReturnsData } = getIncomeTaxReturns();

  const show = (notificationToShow: ApiNotification) => {
    setIsOpen(true);
    setNotification(notificationToShow);
  };

  const onClose = () => {
    setIsOpen(false);
    markAsRead(notification.id);
  };

  useImperativeHandle(forwardedRef, () => ({ ...ref.current, show }));

  useEffect(() => {
    if (isOpen && incomeTaxReturnsData) {
      throwConfetti();
      ref.current?.show();
    }
  }, [isOpen, incomeTaxReturnsData]);

  const jurisdiction = notification?.params?.jurisdiction;
  const incomeTaxReturn = incomeTaxReturnsData?.incomeTaxReturns.find(
    (taxReturn) => taxReturn.jurisdiction === jurisdiction
  );

  if (!incomeTaxReturn || incomeTaxReturn.refundAmount <= 0) return null;

  const { refundAmount, refundAdvanceEligible } = incomeTaxReturn;
  const returnTitle = getReturnTitle(jurisdiction);

  return (
    <Modal ref={ref} onClose={onClose}>
      <Styled.Wrapper>
        <Styled.Heading>
          <Eyebrow>Tax filing</Eyebrow>
          <Heading.H3 tag="h2">
            Your {returnTitle} tax return has been accepted!
          </Heading.H3>
          {refundAdvanceEligible && (
            <Body>
              Start spending a portion of your refund instantly on your Reset
              card.
            </Body>
          )}
        </Styled.Heading>

        <Styled.AmountWrapper>
          <UILabel.Small bold wideCaps color="violet">
            {returnTitle} refund
          </UILabel.Small>
          <Heading.H1 tag="h3" color="azure">
            <Styled.Amount>
              <div>$</div>
              <AnimatedCounter
                delay={0.1}
                value={refundAmount}
                isCurrency={false} // Don't show cents
              />
            </Styled.Amount>
          </Heading.H1>
        </Styled.AmountWrapper>

        <ButtonRouterLink
          fullWidth
          level={ButtonLevel.cta}
          onClick={ref.current?.dismiss}
          to="/dashboard"
        >
          Check Spending Limit
        </ButtonRouterLink>
      </Styled.Wrapper>
    </Modal>
  );
};

export default forwardRef(IncomeTaxReturnAcceptedNotificationModal);
